import React, { createContext, useContext, useState, useCallback, memo, useMemo, useEffect } from 'react';
import { OPEN_SABIT_DEFINITIONS_EVENT } from '../utils/sabitDefinitionAccess';
import * as glb from '../globals.js';

const TabContext = createContext();
const TabActionsContext = createContext();

/** Tüm tab state + aksiyonlar (sekme çubuğu, Content gibi hem state hem aksiyon kullananlar için) */
export const useTabNavigation = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabNavigation must be used within TabProvider');
  }
  return context;
};

/** Sadece aksiyonlar – sekme değişince re-render olmaz (Cari listesi, header, home vb.) */
export const useTabActions = () => {
  const context = useContext(TabActionsContext);
  if (!context) {
    throw new Error('useTabActions must be used within TabProvider');
  }
  return context;
};

export const TabProvider = ({ children }) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const openTab = useCallback((path, title, component) => {
    setTabs(prevTabs => {
      const existingIndex = prevTabs.findIndex(t => t.path === path);
      if (existingIndex >= 0) {
        setActiveTabIndex(existingIndex);
        return prevTabs;
      }
      const MemoComponent = memo(component);
      const newTab = {
        path,
        title,
        component: MemoComponent,
        key: `${path}-${Date.now()}`
      };
      const newTabs = [...prevTabs, newTab];
      setActiveTabIndex(newTabs.length - 1);
      return newTabs;
    });
  }, []);

  const openDetailTab = useCallback((uniqueKey, title, ComponentClass, props = {}) => {
    setTabs(prevTabs => {
      const existingIndex = prevTabs.findIndex(t => t.uniqueKey === uniqueKey);
      if (existingIndex >= 0) {
        setActiveTabIndex(existingIndex);
        return prevTabs;
      }
      const TabComponent = memo(() => <ComponentClass {...props} />);
      const newTab = {
        uniqueKey,
        path: uniqueKey,
        title,
        component: TabComponent,
        key: `${uniqueKey}-${Date.now()}`
      };
      const newTabs = [...prevTabs, newTab];
      setActiveTabIndex(newTabs.length - 1);
      return newTabs;
    });
  }, []);

  const closeTab = useCallback((index) => {
    setTabs(prevTabs => {
      const newTabs = prevTabs.filter((_, i) => i !== index);
      setActiveTabIndex(Math.max(0, newTabs.length - 1));
      return newTabs;
    });
  }, []);

  const closeAllTabs = useCallback(() => {
    setTabs([]);
    setActiveTabIndex(0);
  }, []);

  useEffect(() => {
    const openDefinitions = async (event) => {
      const stTur = Number(event?.detail?.stTur);
      if (!(stTur > 0)) return;
      const group = (glb.default?.sabitgrupLkp || []).find((item) => Number(item.SG_REF) === stTur);
      const groupName = group?.SG_AD || stTur;
      const Sabit = (await import('../pages/SABIT/sabit')).default;
      openDetailTab(`sabit-tanim-${stTur}`, `Tanımlar (${groupName})`, Sabit, { initialStTur: stTur });
    };
    window.addEventListener(OPEN_SABIT_DEFINITIONS_EVENT, openDefinitions);
    return () => window.removeEventListener(OPEN_SABIT_DEFINITIONS_EVENT, openDefinitions);
  }, [openDetailTab]);

  const stateValue = useMemo(() => ({
    tabs,
    activeTabIndex,
    setActiveTabIndex,
    closeTab
  }), [tabs, activeTabIndex, closeTab]);

  const actionsValue = useMemo(() => ({
    openTab,
    openDetailTab,
    closeTab,
    closeAllTabs
  }), [openTab, openDetailTab, closeTab, closeAllTabs]);

  return (
    <TabContext.Provider value={{ ...stateValue, ...actionsValue }}>
      <TabActionsContext.Provider value={actionsValue}>
        {children}
      </TabActionsContext.Provider>
    </TabContext.Provider>
  );
};
